/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useRef, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import useApi from "../../../hooks/useApi";
import { useUser } from "../../../context/UserContext";
import { UserDto } from "../../../types/User";
import { OptionDto, RoleDto } from "../../../types/Role";
import { SEVERITIES } from "../../../data/constants";
import { MessagePriority } from "../../../data/Enums";
import { useToaster } from "../../../hooks/useToaster";
import { INTERNALMESSAGES, MESSAGE_FAIL, MESSAGE_SENT } from "../../../constants/messages";
import MessageDialog from "./MessageDialog";
import { useSendMessage } from "../../../hooks/useSendMessage";
import BackdropProgressBar from "../../../components/ui/BackdropProgressBar";
import SendWithScheduleButton from "../../../components/ui/SendWithScheduleButton";
import { isEmptyMessage } from "../../../utils/commonHelper";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { QuillWithDropRef } from "./QuillWithDrop";
import QuillWithDrop from "./QuillWithDrop";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { RoleUserSelector } from "./RoleUserSelector";

interface NewMessageProps {
  open: boolean;
  onClose: (refresh: boolean) => void;
}

const NewMessage: React.FC<NewMessageProps> = ({ open, onClose }) => {
  const [to, setTo] = useState<OptionDto | null>(null);
  const [toRoles, setToRoles] = useState<RoleDto[]>([]);
  const [toUsers, setToUsers] = useState<UserDto[]>([]);
  const [ccUsers, setCcUsers] = useState<UserDto[]>([]);
  const [subject, setSubject] = useState("");
  const [messageBody, setMessageBody] = useState("");
  const { loading } = useApi();
  const { userId, userName } = useUser();
  const [from, setFrom] = useState(userName);
  const [priority, setPriority] = useState<MessagePriority>(MessagePriority.Normal);
  const [errors, setErrors] = useState<{
    toRoles?: string;
    toUsers?: string;
    subject?: string;
    messageBody?: string;
  }>({});
  const showToaster = useToaster();
  const { sendMessage } = useSendMessage();
  const quillRef = useRef<QuillWithDropRef>(null);
  const [sendMessageLoading, setSendMessageLoading] = useState<boolean>(false);

  const handleClose = (refresh: boolean) => {
    setErrors({});
    setTo(null);
    setToRoles([]);
    setSubject("");
    setMessageBody("");
    setPriority(MessagePriority.Normal);
    onClose(refresh);
  };

  const handleMessageChange = (content: string) => {
    setMessageBody(content);
    const textOnly = content.replace(/<[^>]+>/g, "").trim();
    if (textOnly.length > 0) {
      setErrors((prev) => ({ ...prev, messageBody: undefined }));
    }
  };

  const validate = () => {
    const newErrors: typeof errors = {};
    if (toUsers.length === 0) newErrors.toUsers = INTERNALMESSAGES.USERS_REQUIRED;
    if (!subject.trim()) newErrors.subject = INTERNALMESSAGES.SUBJECT_REQUIRED;
    const bodyText = messageBody.replace(/<[^>]+>/g, "").trim();
    if (isEmptyMessage(messageBody)) {
      newErrors.messageBody = INTERNALMESSAGES.BODY_REQUIRED;
    } else if (bodyText.length < 10) {
      newErrors.messageBody = INTERNALMESSAGES.BODY_MIN_LENGTH;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSend = async (scheduleDate: Date | null) => {
    if (!validate()) return;
    setSendMessageLoading(true);
    const files = quillRef.current?.getAttachments() ?? [];
    const toName = to?.name ?? "";
    const toUserIds = toUsers.map((u) => u.userId);
    const toCacheNames = toUsers.map((u) => u.userName);
    const ccUserIds = ccUsers.map((u) => u.userId);
    const ccCacheNames = ccUsers.map((u) => u.userName);
    const success = await sendMessage(
      userId,
      userName,
      [...toUserIds, ...ccUserIds],
      [...toCacheNames, ...ccCacheNames],
      [...toUsers.map((u) => "To"), ...ccUsers.map((u) => "CC")],
      subject,
      messageBody,
      false,
      priority,
      scheduleDate,
      files,
    );
    setSendMessageLoading(false);
    if (success) {
      showToaster(MESSAGE_SENT(), SEVERITIES.SUCCESS);
      handleClose(true);
    } else {
      showToaster(MESSAGE_FAIL(toName), SEVERITIES.ERROR);
    }
  };

  return (
    <MessageDialog
      open={open}
      onClose={() => handleClose(false)}
      title={
        <>
          {" "}
          <MarkEmailReadIcon sx={{ color: "white", marginRight: 1 }} /> New Message
        </>
      }
      dialogActions={
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <Box display="flex" gap={1}>
              <Select
                size="small"
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value) as MessagePriority)}
                MenuProps={{
                  MenuListProps: {
                    dense: true,
                  },
                }}
              >
                {Object.entries(MessagePriority)
                  .filter(([, value]) => !isNaN(Number(value)))
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  ))}
              </Select>

              <Tooltip title="Attach Files" arrow>
                <IconButton
                  component="label"
                  size="small"
                  sx={{
                    color: "primary.main",
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: "6px",
                    padding: "6px",
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  <AttachFileIcon />
                  <input
                    id="file-upload"
                    type="file"
                    hidden
                    multiple
                    onChange={(e) => {
                      if (!e.target.files) return;
                      const files = Array.from(e.target.files);
                      quillRef.current?.handleManualAttach?.(files);
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>

            <Box display="flex" gap={1}>
              <Button
                onClick={() => handleClose(false)}
                size="small"
                sx={{ display: { xs: "none", sm: "inline-flex" } }}
              >
                Cancel
              </Button>

              <SendWithScheduleButton
                onSend={() => handleSend(null)}
                onScheduleSend={(date) => {
                  handleSend(date);
                }}
              />
            </Box>
          </Box>
        </>
      }
    >
      {<BackdropProgressBar loading={loading || sendMessageLoading} />}
      <Box display="flex" flexDirection="column" gap={1.25} maxWidth={1000} width="100%">
        <TextField
          disabled
          label="From"
          variant="outlined"
          fullWidth
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <>
          <RoleUserSelector
            onRolesChange={(selected) => {
              setToRoles(selected);
              if (selected.length > 0) setErrors((prev) => ({ ...prev, toRoles: undefined }));
            }}
            onUsersChange={(selected) => {
              setToUsers(selected);
              if (selected.length > 0) setErrors((prev) => ({ ...prev, toUsers: undefined }));
            }}
            toLabel=""
            roleError={errors.toRoles}
            userError={errors.toUsers}
          />
          <RoleUserSelector onUsersChange={(selected) => setCcUsers(selected)} toLabel="CC" />
        </>
        <TextField
          id="subject"
          label="Subject"
          variant="outlined"
          fullWidth
          size="small"
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            if (e.target.value.trim()) setErrors((prev) => ({ ...prev, subject: undefined }));
          }}
          error={!!errors.subject}
          helperText={errors.subject}
        />
        <Box sx={{ flexGrow: 1, minHeight: 250 }}>
          {errors.messageBody && (
            <Alert
              severity="error"
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "center",
                px: 2,
              }}
            >
              {errors.messageBody}
            </Alert>
          )}
          <QuillWithDrop ref={quillRef} onChange={handleMessageChange} />
        </Box>
      </Box>
    </MessageDialog>
  );
};

export default NewMessage;
